import { useState } from 'react';

interface Props {
  lang: 'zh' | 'en';
  onClose: () => void;
  onLogin: (user: MallUser) => void;
}

export interface MallUser {
  name: string;
  email: string;
  avatar: string;
  address: string;
  lat: number;
  lng: number;
  level: 'EXPLORER' | 'PIONEER' | 'NEXUS_VIP' | 'GALAXY_ELITE';
  points: number;
}

const LEVEL_COLORS: Record<string, string> = {
  EXPLORER: '#00FFCC',
  PIONEER: '#FFD700',
  NEXUS_VIP: '#FF6B9D',
  GALAXY_ELITE: '#A855F7',
};

const DEMO_USERS: MallUser[] = [
  { name: '星际探索者 · NOVA', email: 'nova@nexus.space', avatar: 'https://public.readdy.ai/ai/img_res/a4d982cd65b7ccda45eaa2e36b6ab1c0.jpg', address: '北京市朝阳区建国路88号', lat: 39.9042, lng: 116.4074, level: 'NEXUS_VIP', points: 88800 },
  { name: '量子旅行者 · ZARA', email: 'zara@nexus.space', avatar: 'https://readdy.ai/api/search-image?query=futuristic%20avatar%20portrait%20sci-fi%20woman%20pink%20neon%20glow%20dark%20background%20ultra%20detailed%208k&width=100&height=100&seq=avatar-2&orientation=squarish', address: '上海市浦东新区陆家嘴金融区', lat: 31.2304, lng: 121.4737, level: 'GALAXY_ELITE', points: 288000 },
  { name: '元宇宙先锋 · ALEX', email: 'alex@nexus.space', avatar: 'https://readdy.ai/api/search-image?query=futuristic%20avatar%20portrait%20sci-fi%20man%20golden%20neon%20glow%20dark%20background%20ultra%20detailed%208k&width=100&height=100&seq=avatar-3&orientation=squarish', address: '广州市天河区珠江新城', lat: 23.1291, lng: 113.2644, level: 'PIONEER', points: 18800 },
];

export default function MallAuthModal({ lang, onClose, onLogin }: Props) {
  const [mode, setMode] = useState<'login' | 'register' | 'demo'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'form' | 'locating' | 'success'>('form');
  const [locatedUser, setLocatedUser] = useState<MallUser | null>(null);

  const handleLogin = () => {
    if (!email || !password) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      const user: MallUser = {
        name: email.split('@')[0] || '星际用户',
        email,
        avatar: 'https://readdy.ai/api/search-image?query=futuristic%20avatar%20portrait%20sci-fi%20person%20neon%20glow%20dark%20background%20ultra%20detailed%208k&width=100&height=100&seq=avatar-login&orientation=squarish',
        address: address || '北京市朝阳区',
        lat: 39.9042 + (Math.random() - 0.5) * 0.1,
        lng: 116.4074 + (Math.random() - 0.5) * 0.1,
        level: 'EXPLORER',
        points: 1000,
      };
      setLocatedUser(user);
      setStep('locating');
      setTimeout(() => {
        setStep('success');
        setTimeout(() => { onLogin(user); onClose(); }, 2000);
      }, 3000);
    }, 1500);
  };

  const handleRegister = () => {
    if (!email || !password || !name) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      const user: MallUser = {
        name,
        email,
        avatar: 'https://readdy.ai/api/search-image?query=futuristic%20avatar%20portrait%20sci-fi%20person%20neon%20glow%20dark%20background%20ultra%20detailed%208k&width=100&height=100&seq=avatar-register&orientation=squarish',
        address: address || '请在Google Earth中定位你的家',
        lat: 39.9042 + (Math.random() - 0.5) * 2,
        lng: 116.4074 + (Math.random() - 0.5) * 2,
        level: 'EXPLORER',
        points: 500,
      };
      setLocatedUser(user);
      setStep('locating');
      setTimeout(() => {
        setStep('success');
        setTimeout(() => { onLogin(user); onClose(); }, 2000);
      }, 3000);
    }, 1500);
  };

  const handleDemoLogin = (user: MallUser) => {
    setLocatedUser(user);
    setStep('locating');
    setTimeout(() => {
      setStep('success');
      setTimeout(() => { onLogin(user); onClose(); }, 2000);
    }, 3000);
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(20px)' }}>
      <div className="relative w-full max-w-lg rounded-3xl overflow-hidden" style={{ background: 'rgba(5,10,20,0.98)', border: '1.5px solid rgba(255,215,0,0.25)' }}>
        {/* Header */}
        <div className="relative p-6 pb-0">
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.05), rgba(255,107,53,0.05))' }} />
          <div className="relative flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,107,53,0.2))', border: '1.5px solid rgba(255,215,0,0.4)' }}>
                <i className="ri-store-3-line text-lg" style={{ color: '#FFD700' }} />
              </div>
              <div>
                <div className="font-black text-sm" style={{ fontFamily: 'Orbitron, sans-serif', color: '#FFD700' }}>NEXUS OMNI MALL</div>
                <div className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{lang === 'zh' ? '星际元宇宙商城 · 用户中心' : 'Interstellar Metaverse Mall · User Center'}</div>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all hover:scale-110" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }}>
              <i className="ri-close-line" />
            </button>
          </div>

          {/* Mode Tabs */}
          <div className="flex gap-1 p-1 rounded-xl mb-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
            {[
              { key: 'login', zh: '登录', en: 'Login' },
              { key: 'register', zh: '注册', en: 'Register' },
              { key: 'demo', zh: '体验账号', en: 'Demo' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setMode(tab.key as typeof mode)} className="flex-1 py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-all" style={{ background: mode === tab.key ? 'rgba(255,215,0,0.15)' : 'transparent', border: mode === tab.key ? '1px solid rgba(255,215,0,0.3)' : '1px solid transparent', color: mode === tab.key ? '#FFD700' : 'rgba(255,255,255,0.35)' }}>
                {lang === 'zh' ? tab.zh : tab.en}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 pt-0">
          {step === 'form' && (
            <>
              {mode === 'demo' ? (
                <div>
                  <div className="text-center mb-5">
                    <div className="text-white/50 text-sm">{lang === 'zh' ? '选择一个体验账号，立即开始星际购物之旅' : 'Choose a demo account to start your interstellar shopping journey'}</div>
                  </div>
                  <div className="space-y-3">
                    {DEMO_USERS.map(user => (
                      <button key={user.email} onClick={() => handleDemoLogin(user)} className="w-full flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all hover:scale-[1.02] text-left" style={{ background: `${LEVEL_COLORS[user.level]}08`, border: `1.5px solid ${LEVEL_COLORS[user.level]}25` }}>
                        <img src={user.avatar} alt={user.name} className="w-12 h-12 rounded-xl object-cover object-top flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-white text-sm mb-0.5">{user.name}</div>
                          <div className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.35)' }}>{user.address}</div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full text-xs font-black" style={{ background: `${LEVEL_COLORS[user.level]}20`, color: LEVEL_COLORS[user.level], border: `1px solid ${LEVEL_COLORS[user.level]}40`, fontSize: '0.6rem', fontFamily: 'Orbitron, sans-serif' }}>{user.level}</span>
                            <span className="text-xs" style={{ color: '#FFD700', fontSize: '0.65rem' }}>{user.points.toLocaleString()} {lang === 'zh' ? '星际积分' : 'Nexus Points'}</span>
                          </div>
                        </div>
                        <i className="ri-arrow-right-line text-sm flex-shrink-0" style={{ color: LEVEL_COLORS[user.level] }} />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {mode === 'register' && (
                    <div>
                      <label className="block text-xs font-bold mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>{lang === 'zh' ? '星际昵称' : 'Nexus Name'}</label>
                      <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={lang === 'zh' ? '输入你的星际昵称...' : 'Enter your Nexus name...'} className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '0.875rem' }} />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-bold mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>{lang === 'zh' ? '星际邮箱' : 'Nexus Email'}</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={lang === 'zh' ? '输入你的邮箱...' : 'Enter your email...'} className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '0.875rem' }} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>{lang === 'zh' ? '量子密码' : 'Quantum Password'}</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={lang === 'zh' ? '输入你的密码...' : 'Enter your password...'} className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '0.875rem' }} />
                  </div>
                  {mode === 'register' && (
                    <div>
                      <label className="block text-xs font-bold mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                        <i className="ri-map-pin-line mr-1" style={{ color: '#FFD700' }} />
                        {lang === 'zh' ? '家庭地址（用于Google Earth定位）' : 'Home Address (for Google Earth location)'}
                      </label>
                      <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder={lang === 'zh' ? '输入你的家庭地址...' : 'Enter your home address...'} className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '0.875rem' }} />
                      <div className="mt-1.5 text-xs" style={{ color: 'rgba(255,215,0,0.5)' }}>
                        <i className="ri-shield-check-line mr-1" />
                        {lang === 'zh' ? '地址仅用于物流定位，量子加密保护，绝不泄露' : 'Address used only for logistics, quantum-encrypted, never shared'}
                      </div>
                    </div>
                  )}
                  <button onClick={mode === 'login' ? handleLogin : handleRegister} disabled={loading} className="w-full py-3.5 rounded-2xl font-black text-sm cursor-pointer whitespace-nowrap transition-all hover:scale-[1.02] flex items-center justify-center gap-2" style={{ background: loading ? 'rgba(255,215,0,0.1)' : 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,107,53,0.2))', border: '1.5px solid rgba(255,215,0,0.4)', color: '#FFD700', fontFamily: 'Orbitron, sans-serif', fontSize: '0.8rem' }}>
                    {loading ? (
                      <><div className="w-4 h-4 rounded-full border-2 border-yellow-400/30 border-t-yellow-400 animate-spin" />{lang === 'zh' ? '量子验证中...' : 'Quantum Verifying...'}</>
                    ) : (
                      <><i className={mode === 'login' ? 'ri-login-circle-line' : 'ri-user-add-line'} />{lang === 'zh' ? (mode === 'login' ? '登录星际商城' : '注册星际账号') : (mode === 'login' ? 'Login to Nexus Mall' : 'Register Nexus Account')}</>
                    )}
                  </button>
                </div>
              )}

              {/* Google Earth Notice */}
              <div className="mt-5 p-4 rounded-2xl" style={{ background: 'rgba(0,255,204,0.05)', border: '1px solid rgba(0,255,204,0.15)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,255,204,0.1)', border: '1px solid rgba(0,255,204,0.3)' }}>
                    <i className="ri-earth-line text-sm" style={{ color: '#00FFCC' }} />
                  </div>
                  <div>
                    <div className="font-bold text-sm mb-1" style={{ color: '#00FFCC' }}>{lang === 'zh' ? 'Google Earth 自动定位' : 'Google Earth Auto-Location'}</div>
                    <div className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      {lang === 'zh' ? '登录后，Google Earth将自动定位到你的家庭地址，实现精准星际物流配送体验。' : 'After login, Google Earth will automatically locate your home address for precise interstellar logistics delivery.'}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 'locating' && locatedUser && (
            <div className="text-center py-8">
              <div className="relative w-24 h-24 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full" style={{ border: '3px solid rgba(0,255,204,0.2)', animation: 'spin 3s linear infinite' }} />
                <div className="absolute inset-3 rounded-full" style={{ border: '2px solid rgba(0,255,204,0.5)', animation: 'spinReverse 2s linear infinite' }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <i className="ri-earth-line text-3xl" style={{ color: '#00FFCC' }} />
                </div>
              </div>
              <div className="font-black text-xl text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                {lang === 'zh' ? 'Google Earth 定位中...' : 'Google Earth Locating...'}
              </div>
              <div className="text-white/40 text-sm mb-4">{lang === 'zh' ? '正在将你的家庭地址同步到星际物流系统' : 'Syncing your home address to the interstellar logistics system'}</div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'rgba(0,255,204,0.08)', border: '1px solid rgba(0,255,204,0.2)' }}>
                <i className="ri-map-pin-line text-sm" style={{ color: '#00FFCC' }} />
                <span className="text-sm" style={{ color: '#00FFCC' }}>{locatedUser.address}</span>
              </div>
              <div className="mt-4 text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
                {lang === 'zh' ? `坐标: ${locatedUser.lat.toFixed(4)}°N, ${locatedUser.lng.toFixed(4)}°E` : `Coordinates: ${locatedUser.lat.toFixed(4)}°N, ${locatedUser.lng.toFixed(4)}°E`}
              </div>
            </div>
          )}

          {step === 'success' && locatedUser && (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-5 rounded-2xl overflow-hidden" style={{ border: `2px solid ${LEVEL_COLORS[locatedUser.level]}50` }}>
                <img src={locatedUser.avatar} alt={locatedUser.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="font-black text-xl text-white mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                {lang === 'zh' ? '欢迎回来！' : 'Welcome Back!'}
              </div>
              <div className="text-white/60 text-base mb-3">{locatedUser.name}</div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: `${LEVEL_COLORS[locatedUser.level]}15`, border: `1px solid ${LEVEL_COLORS[locatedUser.level]}40` }}>
                <i className="ri-vip-crown-line text-sm" style={{ color: LEVEL_COLORS[locatedUser.level] }} />
                <span className="text-sm font-black" style={{ color: LEVEL_COLORS[locatedUser.level], fontFamily: 'Orbitron, sans-serif' }}>{locatedUser.level}</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm" style={{ color: '#00FFCC' }}>
                <i className="ri-check-double-line" />
                {lang === 'zh' ? 'Google Earth 定位成功！正在跳转...' : 'Google Earth located! Redirecting...'}
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes spinReverse { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
      `}</style>
    </div>
  );
}
